import React from "react";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";

const CONDITION_COLORS = [
  "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899",
  "#06b6d4", "#f97316", "#14b8a6", "#a855f7", "#e11d48", "#84cc16",
];

/**
 * Bipolar spectrum chart for emotional valence.
 * Shows each condition as a bar extending from the midpoint (4),
 * colored by direction: blue/cool for negative, warm/red for positive.
 * Midpoint = neutral/mixed.
 */
const ValenceSpectrumChart = ({ stats, conditions, height = 220 }) => {
  if (!stats?.byCondition || Object.keys(stats.byCondition).length === 0) {
    return <p className="text-gray-500 text-sm">No data</p>;
  }

  const conditionLabels = {};
  conditions.forEach((c) => {
    conditionLabels[c.conditionId] = c.label || c.conditionId;
  });

  const midpoint = 50;

  const chartData = conditions.map((c, i) => {
    const m = stats.byCondition[c.conditionId]?.mean || midpoint;
    const sd = stats.byCondition[c.conditionId]?.sd || 0;
    const n = stats.byCondition[c.conditionId]?.n || 0;
    return {
      name: conditionLabels[c.conditionId] || c.conditionId,
      value: m - midpoint, // Offset from midpoint (negative = hostile, positive = warm)
      rawMean: m,
      sd,
      n,
      colorIndex: i,
    };
  });

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const valence = d.rawMean < 40 ? "Negative" : d.rawMean > 60 ? "Positive" : "Mixed";
    return (
      <div className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-xs shadow-lg">
        <p className="text-gray-300 font-medium">{d.name}</p>
        <p className="text-gray-400">M = {d.rawMean?.toFixed(2)} (SD = {d.sd?.toFixed(2)}, n = {d.n})</p>
        <p className={d.rawMean < 3.5 ? "text-blue-400" : d.rawMean > 4.5 ? "text-orange-400" : "text-gray-400"}>
          {valence} valence
        </p>
      </div>
    );
  };

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            type="number"
            domain={[-50, 50]}
            stroke="#9ca3af"
            tick={{ fill: "#9ca3af", fontSize: 10 }}
            tickFormatter={(v) => (v + midpoint).toFixed(0)}
          />
          <YAxis
            type="category"
            dataKey="name"
            stroke="#9ca3af"
            tick={{ fill: "#9ca3af", fontSize: 11 }}
            width={95}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine x={0} stroke="#6b7280" strokeWidth={2} label={{ value: "Neutral", fill: "#6b7280", fontSize: 10, position: "top" }} />
          <Bar dataKey="value" barSize={20}>
            {chartData.map((entry, i) => (
              <Cell
                key={i}
                fill={entry.value < -5 ? "#60a5fa" : entry.value > 5 ? "#f97316" : "#6b7280"}
                fillOpacity={0.8}
              />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
      <div className="flex justify-between text-xs text-gray-500 mt-1 px-2">
        <span>0: Negative (hostile, contemptuous)</span>
        <span>100: Positive (warm, supportive)</span>
      </div>
    </div>
  );
};

export default ValenceSpectrumChart;
